import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { useAuthStore } from '@/app/store/auth'
import { isCredentialAnswer } from './credential-answer'
import { askForStepUp, currentStepUp, forgetStepUp, stepUpWanted, STEP_UP_HEADER } from './step-up'
import { rateLimitRetryDelay } from './rate-limit-retry'

// Create axios instance
const api: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '',
    timeout: 30000,
    withCredentials: true, // Important for httpOnly cookies
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
})

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false
let failedQueue: Array<{
    resolve: (value: any) => void
    reject: (error: any) => void
}> = []

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error)
        } else {
            resolve(token)
        }
    })

    failedQueue = []
}

// Request interceptor
api.interceptors.request.use(
    (config) => {
        // Add Authorization header if access token is available and MFA is not required
        const { accessToken, mfaRequired, isAuthenticated, mfaSetupToken, mfaToken } = useAuthStore.getState()

        // Block protected API calls until token is ready
        // Allow public endpoints (app config, health checks, etc.)
        const isApiCall = config.url?.startsWith('/api/')
        const isPublicEndpoint = config.url === '/api/app/config' || config.url === '/health'
        if (isApiCall && !isPublicEndpoint && (!accessToken || mfaRequired || !isAuthenticated)) {
            // Silently cancel the request to avoid spurious 403s during auth setup
            // Consumers should guard queries with auth-ready checks
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            return Promise.reject(new axios.Cancel('auth-not-ready'))
        }
        if (accessToken && !mfaRequired) {
            config.headers.Authorization = `Bearer ${accessToken}`
            // Only log in development mode to reduce console noise
            if (import.meta.env.DEV && config.url?.startsWith('/api/')) {
                console.log('🔐 Added Authorization header:', {
                    url: config.url,
                    hasToken: true
                })
            }
        } else {
            // Only warn for API calls that should have auth (not login/totp endpoints or public endpoints)
            if (config.url?.startsWith('/api/') && !config.url?.includes('/auth/') && !isPublicEndpoint) {
                console.warn('🔐 Missing Authorization header for API call:', config.url)
            }
        }

        // Step-up: adding or removing one of your own sign-in devices is not done on the session
        // token but on the short-lived token earned by proving a device you already have
        // (features/devices). The caller passes it on the request; it replaces the session token
        // for that one call and goes nowhere else.
        const stepUpToken = (config as { stepUpToken?: string }).stepUpToken
        if (stepUpToken) {
            config.headers.Authorization = `Bearer ${stepUpToken}`
        } else if (!config.headers[STEP_UP_HEADER]) {
            // A proof of a device earned in the last few minutes travels with every ordinary call:
            // the server looks at it only on the high-risk routes (Stage 7; lib/api/step-up.ts).
            const proof = currentStepUp()
            if (proof) config.headers[STEP_UP_HEADER] = proof
        }

        // MFA enrollment: the TOTP setup endpoints now require a token. Mid-login
        // (no session token yet, mfaRequired=true) we carry the short-lived
        // enrollment token instead. A real session token (authenticated re-enrol)
        // takes precedence if already set above.
        const MFA_SETUP_ENDPOINTS = ['/auth/totp/setup', '/auth/totp/initial-setup', '/auth/totp/verify']
        if (config.url && MFA_SETUP_ENDPOINTS.includes(config.url) && mfaSetupToken && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${mfaSetupToken}`
        }

        // MFA challenge: the second factor. /auth/login verified the password and handed back a
        // short-lived challenge token, and it is now the ONLY thing that says who is answering —
        // these endpoints no longer take an email. Mid-login there is no session token, so
        // without this the code goes up unattributed and the server answers 401.
        const isMfaChallengeEndpoint = config.url === '/auth/totp/challenge'
        if (isMfaChallengeEndpoint && mfaToken && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${mfaToken}`
        }

        // Add CSRF token if available
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        if (csrfToken) {
            config.headers['X-CSRF-Token'] = csrfToken
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = generateRequestId()

        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Response interceptor
api.interceptors.response.use(
    (response: AxiosResponse) => {
        return response
    },
    async (error) => {
        // Handle cancel errors (from request interceptor) - just reject them
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }

        const originalRequest = error.config

        // Skip processing if there's no request config (cancel errors, etc.)
        if (!originalRequest) {
            return Promise.reject(error)
        }

        // Handle 401/403 auth failures (but skip for logout requests)
        const isAuthFailure = (error.response?.status === 401) ||
            (error.response?.status === 403 && typeof error.response?.data?.message === 'string' && error.response.data.message.toLowerCase().includes('authentication'))
        if (isAuthFailure && !originalRequest._retry && !originalRequest.url?.includes('/auth/logout')) {
            // A 401 that means "that code was not right" is the answer: it goes to the screen once,
            // as it is. Refreshing and re-sending would count a wrong code twice, and with no
            // session to refresh (mid sign-in) would sign the user out (credential-answer.ts).
            if (isCredentialAnswer(originalRequest)) {
                return Promise.reject(error)
            }
            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject })
                }).then(token => {
                    originalRequest.headers.Authorization = `Bearer ${token}`
                    return api(originalRequest)
                }).catch(err => {
                    return Promise.reject(err)
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                // Try to refresh using the backend's refresh endpoint
                const { refreshToken } = useAuthStore.getState()
                const refreshResponse = await api.post('/auth/refresh', { refreshToken })
                // Support both direct and wrapped success responses
                const refreshedToken = refreshResponse.data?.accessToken ?? refreshResponse.data?.data?.accessToken

                // If refresh was successful, process queued requests
                processQueue(null, refreshedToken)
                isRefreshing = false

                // Retry the original request
                originalRequest.headers.Authorization = `Bearer ${refreshedToken}`
                // Update store tokens keeping existing refresh token
                const { setTokens } = useAuthStore.getState()
                if (refreshedToken) {
                    setTokens(refreshedToken, refreshToken || '', 15 * 60)
                }
                return api(originalRequest)
            } catch (refreshError) {
                // Refresh failed, process queue with error
                processQueue(refreshError, null)
                isRefreshing = false

                // Clear tokens and logout user
                const { forceLogout } = useAuthStore.getState()
                forceLogout()

                return Promise.reject(refreshError)
            }
        }

        // The server wants a fresh proof of a device before this action (Stage 7). Ask the user
        // for it and send the request again, ONCE. If they cannot or will not, the refusal goes to
        // the caller as it is. Not for calls made on a token of their own (the devices screens
        // ask for themselves).
        const wanted = stepUpWanted(error)
        if (wanted && !originalRequest._steppedUp && !originalRequest.stepUpToken) {
            // The proof that was sent, if any, was not good enough: do not offer it again.
            forgetStepUp()
            const proof = await askForStepUp(wanted === 'strong')
            if (proof) {
                originalRequest._steppedUp = true
                originalRequest.headers[STEP_UP_HEADER] = proof
                return api(originalRequest)
            }
            return Promise.reject(error)
        }

        // 429: the server asked us to slow down. A read is retried once the short wait it asks for
        // has passed; anything else (a write, a sign-in, a long wait) goes to the caller as it is.
        const retryIn = rateLimitRetryDelay(error, originalRequest)
        if (retryIn != null) {
            originalRequest._rateLimitRetries = (originalRequest._rateLimitRetries ?? 0) + 1
            await new Promise((resolve) => setTimeout(resolve, retryIn))
            return api(originalRequest)
        }

        // Handle logout requests - always clear state even on 401
        if (originalRequest.url?.includes('/auth/logout')) {
            const { forceLogout } = useAuthStore.getState()
            forceLogout()
            // Don't redirect here, let the logout hook handle it
            return Promise.reject(error)
        }

        // Handle 419 CSRF token mismatch
        if (error.response?.status === 419) {
            const { forceLogout } = useAuthStore.getState()
            forceLogout()
            return Promise.reject(error)
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
            console.warn('Access forbidden:', error.response.data)
        }

        // Emit global error event for toast notifications (non-auth errors only)
        if (error.response && error.response.status >= 500) {
            const msg = error.response?.data?.message || error.response?.data?.error || 'An unexpected server error occurred'
            window.dispatchEvent(new CustomEvent('api-error', { detail: { status: error.response.status, message: msg, url: originalRequest?.url } }))
        } else if (!error.response && error.request) {
            window.dispatchEvent(new CustomEvent('api-error', { detail: { status: 0, message: 'Unable to reach the server. Please check your connection.', url: originalRequest?.url } }))
        }

        return Promise.reject(error)
    }
)

// Helper function to generate request ID
function generateRequestId(): string {
    return Math.random().toString(36).substr(2, 9)
}

// Helper function to handle API errors
export function handleApiError(error: any): string {
    if (error.response?.data?.message) {
        return error.response.data.message
    }

    if (error.response?.data?.detail) {
        return error.response.data.detail
    }

    if (error.message) {
        return error.message
    }

    return 'An unexpected error occurred'
}

// Helper function to check if error is network related
export function isNetworkError(error: any): boolean {
    return !error.response && error.request
}

// Helper function to check if error is timeout
export function isTimeoutError(error: any): boolean {
    return error.code === 'ECONNABORTED' || error.message?.includes('timeout')
}

export default api



